import React from "react";
import { Box, Typography, Button, Container, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

const RotatingGlobe = ({ theme }) => {
  const [rotation, setRotation] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRotation((prev) => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Generate dots for globe
  const dots = React.useMemo(() => {
    const dotArray = [];
    const radius = 120;
    const numDots = 200;

    for (let i = 0; i < numDots; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);

      dotArray.push({ x, y, z, theta });
    }
    return dotArray;
  }, []);

  return (
    <Box
      sx={{
        width: 280,
        height: 280,
        position: "relative",
        perspective: "1000px",
      }}
    >
      {dots.map((dot, index) => {
        const rotatedTheta = dot.theta + (rotation * Math.PI) / 180;
        const x =
          dot.x * Math.cos(rotatedTheta) - dot.z * Math.sin(rotatedTheta);
        const z =
          dot.x * Math.sin(rotatedTheta) + dot.z * Math.cos(rotatedTheta);

        const scale = (z + 120) / 240;
        const opacity = Math.max(0.2, scale);

        return (
          <Box
            key={index}
            sx={{
              position: "absolute",
              width: 4,
              height: 4,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${dot.y}px)`,
              transform: `scale(${scale})`,
              opacity: opacity,
              transition: "all 0.05s linear",
            }}
          />
        );
      })}
    </Box>
  );
};

const CTA = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Grid container spacing={6} alignItems="center">
            {/* Left Side - Globe Animation */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RotatingGlobe theme={theme} />
                <Typography
                  variant="h4"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    textAlign: "center",
                    width: "80%",
                    zIndex: 10,
                  }}
                >
                  Let us help you
                  <br />
                  Manage your Finances
                </Typography>
              </Box>
            </Grid>

            
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 3,
                  }}
                >
                  Ready to Transform Your Financial Management?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  At F-AI, we are dedicated to providing you with comprehensive
                  financial solutions to help you manage your financial records
                  and reporting requirements. Contact us today to learn more.
                </Typography>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  color="primary"
                  sx={{
                    borderRadius: 2,
                    px: 5,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: theme.shadows[8],
                    "&:hover": {
                      boxShadow: theme.shadows[12],
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CTA;
